import * as React from "react";

import {
  type CollisionDescriptor,
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { api } from "@repo/backend";
import type { Id } from "@repo/backend/convex/_generated/dataModel";
import { Button } from "@repo/ui/components/button";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@repo/ui/components/context-menu";
import { Input } from "@repo/ui/components/input";
import { Label } from "@repo/ui/components/label";
import {
  ScheduleTimeline,
  type ScheduleTimelineDragData,
  type ScheduleTimelineGroup,
  type ScheduleTimelineInteraction,
  type ScheduleTimelineItem,
  type ScheduleTimelinePreview,
  type ScheduleTimelineRenderers,
  type ScheduleTimelineResizeData,
  type ScheduleTimelineRowDropData,
  type ScheduleTimelineValue,
} from "@repo/ui/components/schedule-timeline";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@repo/ui/components/sheet";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { addDays, differenceInCalendarDays, format, startOfDay } from "date-fns";
import {
  CalendarDaysIcon,
  GripVerticalIcon,
  InfoIcon,
  LockIcon,
  LockOpenIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";

import { GameDetailsSheet } from "@/components/ewc-calendar/game-details-sheet";
import { useRoleAccess } from "@/lib/role-access";

export const Route = createFileRoute("/_authed/ewc-calendar")({
  head: () => ({ meta: [{ title: "EWC Calendar" }] }),
  component: RouteComponent,
});

const TIMELINE_YEAR = 2026;
const TIMELINE_START = new Date(TIMELINE_YEAR, 5, 20);
const TIMELINE_END = new Date(TIMELINE_YEAR, 7, 23);
const DEFAULT_DAY_WIDTH = 100;
const MIN_DAY_WIDTH = 50;
const MAX_DAY_WIDTH = 260;
const DAY_WIDTH_STORAGE_KEY = "game-title-schedule-day-width-v2";
const TIMELINE_ROW_HEIGHT = 56;
const TIMELINE_GROUP_COLUMN_WIDTH = 220;
const EDGE_SCROLL_ZONE = 72;
const MAX_EDGE_SCROLL_SPEED = 18;
const TIMELINE_DAY_COUNT = differenceInCalendarDays(TIMELINE_END, TIMELINE_START) + 1;
const INITIAL_SCROLL_DATE = startOfDay(addDays(new Date(), -1));
const TIMELINE_RANGE = {
  kind: "date",
  start: TIMELINE_START,
  end: addDays(TIMELINE_END, 1),
  unit: "day",
} as const;
const TIMELINE_LABELS = {
  groupColumn: "Stage",
  rowColumn: "",
  empty: "No stages configured.",
  emptyRow: "Drop a game title here",
  itemDelete: "Remove from schedule",
};
const TIMELINE_HEADER_BANDS = [
  {
    id: "week-0-0",
    label: "Week 0.0",
    description: "20.06 – 28.06",
    start: new Date(TIMELINE_YEAR, 5, 20),
    end: new Date(TIMELINE_YEAR, 5, 29),
  },
  {
    id: "week-0",
    label: "Week 0",
    description: "29.06 – 05.07",
    start: new Date(TIMELINE_YEAR, 5, 29),
    end: new Date(TIMELINE_YEAR, 6, 6),
  },
  {
    id: "week-1",
    label: "Week 1",
    description: "06.07 – 12.07",
    start: new Date(TIMELINE_YEAR, 6, 6),
    end: new Date(TIMELINE_YEAR, 6, 13),
  },
  {
    id: "week-2",
    label: "Week 2",
    description: "13.07 – 19.07",
    start: new Date(TIMELINE_YEAR, 6, 13),
    end: new Date(TIMELINE_YEAR, 6, 20),
  },
  {
    id: "week-3",
    label: "Week 3",
    description: "20.07 – 26.07",
    start: new Date(TIMELINE_YEAR, 6, 20),
    end: new Date(TIMELINE_YEAR, 6, 27),
  },
  {
    id: "week-4",
    label: "Week 4",
    description: "27.07 – 02.08",
    start: new Date(TIMELINE_YEAR, 6, 27),
    end: new Date(TIMELINE_YEAR, 7, 3),
  },
  {
    id: "week-5",
    label: "Week 5",
    description: "03.08 – 09.08",
    start: new Date(TIMELINE_YEAR, 7, 3),
    end: new Date(TIMELINE_YEAR, 7, 10),
  },
  {
    id: "week-6",
    label: "Week 6",
    description: "10.08 – 16.08",
    start: new Date(TIMELINE_YEAR, 7, 10),
    end: new Date(TIMELINE_YEAR, 7, 17),
  },
  {
    id: "week-7",
    label: "Week 7",
    description: "17.08 – 23.08",
    start: new Date(TIMELINE_YEAR, 7, 17),
    end: new Date(TIMELINE_YEAR, 7, 24),
  },
];

const SECTION_ORDER: Record<string, number> = {
  "Event Coverage": 0,
  "Media Day": 1,
};

const STAGE_NAME_LABELS: Record<string, string> = {
  ARENA: "STAGE 1: stc ArenaS",
  GRID: "STAGE 2: Qiddiya City Grid",
  ATRIUM: "STAGE 3: Lenovo Legion Gauntlet",
  PAVILION: "STAGE 4: Allianz Vault",
  "SECONDARY 1": "Qiddiya City Battleground 1",
  "SECONDARY 2": "Qiddiya City Battleground 2",
  "SECONDARY 3": "Qiddiya City Battleground 3",
  "LCQ-1": "stc Contenders",
  "LCQ-2": "stc Contenders",
  "LCQ 1": "stc Contenders",
  "LCQ 2": "stc Contenders",
};

const getStageNameLabel = (stageName: string) => STAGE_NAME_LABELS[stageName] ?? stageName;

type TitleForm = {
  id?: string;
  name: string;
  color: string;
};

type StageRenameForm = {
  oldName: string;
  newName: string;
};

type GameTitle = NonNullable<ReturnType<typeof useQuery<typeof api.gameTitles.list>>>[number];

type ScheduleItemData = {
  scheduleItemId: string;
  gameTitleId: string;
  color: string;
};

type TimelineItem = ScheduleTimelineItem<ScheduleItemData>;
type TimelineGroup = ScheduleTimelineGroup<ScheduleItemData, unknown, StageGroupData>;

type StageGroupData = {
  section: string;
  stageName: string;
};

type TitleDragData = {
  type: "game-title";
  id: string;
  name: string;
  color: string;
};

type TimelineItemDragData = ScheduleTimelineDragData<ScheduleItemData>;
type TimelineResizeData = ScheduleTimelineResizeData<ScheduleItemData>;
type LibraryDeleteDropData = { type: "game-title-library-delete" };

type DragData =
  | TitleDragData
  | TimelineItemDragData
  | TimelineResizeData
  | ScheduleTimelineRowDropData
  | LibraryDeleteDropData;

const getGameColorStyle = (color: string) => ({
  backgroundColor: `${color}33`,
  borderColor: `${color}AA`,
});

const getSegmentedGameColorStyle = (color: string, dayWidth: number, durationDays: number) => ({
  ...getGameColorStyle(color),
  backgroundImage:
    durationDays > 1
      ? `repeating-linear-gradient(to right, transparent 0, transparent ${dayWidth - 2}px, ${color}99 ${dayWidth - 2}px, ${color}99 ${dayWidth}px)`
      : undefined,
});

const clampDayWidth = (width: number) =>
  Math.max(MIN_DAY_WIDTH, Math.min(MAX_DAY_WIDTH, Math.round(width)));

const getInitialDayWidth = () => {
  if (typeof window === "undefined") return DEFAULT_DAY_WIDTH;
  const stored = Number(window.localStorage.getItem(DAY_WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) ? clampDayWidth(stored) : DEFAULT_DAY_WIDTH;
};

const clampDayIndex = (day: number, max = TIMELINE_DAY_COUNT - 1) =>
  Math.max(0, Math.min(max, day));

const dateValue = (value: ScheduleTimelineValue) =>
  value instanceof Date ? value : new Date(value);

const getItemDurationDays = (item: TimelineItem) =>
  Math.max(1, differenceInCalendarDays(dateValue(item.end), dateValue(item.start)));

const buildLanes = (items: TimelineItem[]) => {
  const lanes: TimelineItem[][] = [];

  for (const item of items.toSorted(
    (a, b) => dateValue(a.start).getTime() - dateValue(b.start).getTime(),
  )) {
    const lane = lanes.find(
      (candidate) => dateValue(candidate.at(-1)?.end ?? TIMELINE_START) <= dateValue(item.start),
    );
    if (lane) lane.push(item);
    else lanes.push([item]);
  }

  return lanes.length ? lanes : [[]];
};

const getDropTarget = (event: DragMoveEvent | DragEndEvent) => {
  const collision = event.collisions?.[0] as CollisionDescriptor | undefined;
  const container = collision?.data.droppableContainer;
  const data = container?.data.current as DragData | undefined;
  const rect = container?.rect.current ?? event.over?.rect;

  if (!data || !rect) return;
  return { data, rect };
};

const getPreview = (
  event: DragMoveEvent | DragEndEvent,
  dayWidth: number,
  pointerX: number | null,
) => {
  const active = event.active.data.current as DragData | undefined;
  const target = getDropTarget(event);
  const over = target?.data;
  if (!active) return;

  if (active.type === "game-title" && over?.type === "schedule-timeline-row") {
    const rowRect = target?.rect;
    if (pointerX === null || !rowRect) return;

    const dayIndex = clampDayIndex(Math.floor((pointerX - rowRect.left) / dayWidth));
    const start = addDays(TIMELINE_START, dayIndex);
    return {
      groupId: over.groupId,
      rowId: over.rowId,
      title: active.name,
      mode: "ghost",
      start,
      end: addDays(start, 1),
      style: getGameColorStyle(active.color),
    } satisfies ScheduleTimelinePreview;
  }

  if (
    active.type === "schedule-timeline-item" &&
    active.rangeKind === "date" &&
    over?.type === "schedule-timeline-row"
  ) {
    const durationDays = getItemDurationDays(active.item);
    const initialDay = differenceInCalendarDays(dateValue(active.item.start), TIMELINE_START);
    const dayIndex = clampDayIndex(
      initialDay + Math.round(event.delta.x / dayWidth),
      TIMELINE_DAY_COUNT - durationDays,
    );
    const start = addDays(TIMELINE_START, dayIndex);
    return {
      id: active.item.id,
      groupId: over.groupId,
      rowId: over.rowId,
      title: active.item.title,
      mode: "ghost",
      start,
      end: addDays(start, durationDays),
      style: active.item.style,
    } satisfies ScheduleTimelinePreview;
  }

  if (active.type === "schedule-timeline-resize" && active.rangeKind === "date") {
    const initialStart = differenceInCalendarDays(dateValue(active.item.start), TIMELINE_START);
    const initialEnd = differenceInCalendarDays(dateValue(active.item.end), TIMELINE_START);
    const deltaDays = Math.round(event.delta.x / dayWidth);
    const startIndex =
      active.edge === "start"
        ? clampDayIndex(initialStart + deltaDays, initialEnd - 1)
        : initialStart;
    const endIndex =
      active.edge === "end"
        ? Math.max(startIndex + 1, Math.min(TIMELINE_DAY_COUNT, initialEnd + deltaDays))
        : initialEnd;

    return {
      id: active.item.id,
      groupId: active.groupId,
      rowId: active.rowId,
      title: active.item.title,
      mode: "solid",
      start: addDays(TIMELINE_START, startIndex),
      end: addDays(TIMELINE_START, endIndex),
      style: active.item.style,
    } satisfies ScheduleTimelinePreview;
  }
};

// dateOnlyFromTimestamp and timestampFromDateOnly preserve the backend's
// date-only == noon-UTC invariant: stored schedule dates use T12:00:00.000Z,
// so we read UTC Y/M/D and write noon UTC to round-trip safely across timezones.
const dateOnlyFromTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const timestampFromDateOnly = (date: Date) =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12);

const updateDropPreview = (
  node: HTMLDivElement | null,
  preview: ScheduleTimelinePreview | undefined,
  rowRect: { left: number; top: number; height: number } | undefined,
  dayWidth: number,
) => {
  if (!node) return;
  if (!preview || !rowRect) {
    node.style.display = "none";
    return;
  }

  const startDay = differenceInCalendarDays(dateValue(preview.start), TIMELINE_START);
  const durationDays = Math.max(
    1,
    differenceInCalendarDays(dateValue(preview.end), dateValue(preview.start)),
  );
  const backgroundColor = preview.style?.backgroundColor;
  const backgroundImage = preview.style?.backgroundImage;
  const borderColor = preview.style?.borderColor;

  node.textContent = preview.title ?? "";
  node.style.display = "flex";
  node.style.transform = `translate3d(${rowRect.left + startDay * dayWidth}px, ${rowRect.top + 6}px, 0)`;
  node.style.width = `${Math.max(42, durationDays * dayWidth - 8)}px`;
  node.style.height = `${Math.max(1, rowRect.height - 12)}px`;
  node.style.backgroundColor = typeof backgroundColor === "string" ? backgroundColor : "";
  node.style.backgroundImage = typeof backgroundImage === "string" ? backgroundImage : "";
  node.style.borderColor = typeof borderColor === "string" ? borderColor : "";
  node.style.borderStyle = preview.mode === "solid" ? "solid" : "dashed";
  node.style.opacity = preview.mode === "solid" ? "1" : "0.72";
};

function useStageGroups() {
  const stages = useQuery(api.schedule.listStages);

  return React.useMemo(() => {
    if (!stages)
      return {
        groups: [],
        stageMap: new Map<string, { section: string; order: number }>(),
        isLoading: true,
      };

    const stageMap = new Map<string, { section: string; order: number }>();
    for (const stage of stages) {
      const existing = stageMap.get(stage.name);
      if (!existing || stage.order < existing.order) {
        stageMap.set(stage.name, { section: stage.section ?? "Other", order: stage.order });
      }
    }

    const sectionMap = new Map<string, string[]>();
    for (const [name, { section }] of stageMap) {
      sectionMap.set(section, [...(sectionMap.get(section) ?? []), name]);
    }

    for (const names of sectionMap.values()) {
      names.sort((a, b) => (stageMap.get(a)?.order ?? 0) - (stageMap.get(b)?.order ?? 0));
    }

    return {
      groups: [...sectionMap.entries()]
        .map(([section, sectionStages]) => ({ section, stages: sectionStages }))
        .toSorted((a, b) => (SECTION_ORDER[a.section] ?? 2) - (SECTION_ORDER[b.section] ?? 2)),
      stageMap,
      isLoading: false,
    };
  }, [stages]);
}

function RouteComponent() {
  const confirm = useConfirmationDialog();
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const rawGames = useQuery(api.gameTitles.list);
  const scheduleItems = useQuery(api.gameTitles.listScheduleItems);
  const createTitle = useMutation(api.gameTitles.create);
  const updateTitle = useMutation(api.gameTitles.update).withOptimisticUpdate(
    (store, { id, ...updates }) => {
      const games = store.getQuery(api.gameTitles.list, {});
      const current = games?.find((game) => game._id === id);
      if (!games || !current) return;
      const patch = Object.fromEntries(
        Object.entries(updates)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [
            key,
            (key === "stageName" || key === "section") && value === null ? undefined : value,
          ]),
      );
      const nextGame = { ...current, ...patch, updatedAt: Date.now() };
      store.setQuery(
        api.gameTitles.list,
        {},
        games.map((game) => (game._id === id ? nextGame : game)),
      );
      const details = store.getQuery(api.gameTitles.getDetails, { id });
      if (details)
        store.setQuery(api.gameTitles.getDetails, { id }, { ...details, game: nextGame });
      const cachedItems = store.getQuery(api.gameTitles.listScheduleItems, {});
      if (cachedItems) {
        store.setQuery(
          api.gameTitles.listScheduleItems,
          {},
          cachedItems.map((item) =>
            item.gameTitleId === id && item.game
              ? { ...item, game: { ...item.game, ...patch } }
              : item,
          ),
        );
      }
    },
  );
  const removeTitle = useMutation(api.gameTitles.remove).withOptimisticUpdate((store, { id }) => {
    const games = store.getQuery(api.gameTitles.list, {});
    if (games)
      store.setQuery(
        api.gameTitles.list,
        {},
        games.filter((game) => game._id !== id),
      );
    const details = store.getQuery(api.gameTitles.getDetails, { id });
    if (details) store.setQuery(api.gameTitles.getDetails, { id }, null);
    const cachedItems = store.getQuery(api.gameTitles.listScheduleItems, {});
    if (cachedItems) {
      store.setQuery(
        api.gameTitles.listScheduleItems,
        {},
        cachedItems.filter((item) => item.gameTitleId !== id),
      );
    }
  });
  const createScheduleItem = useMutation(api.gameTitles.createScheduleItem);
  const updateScheduleItem = useMutation(api.gameTitles.updateScheduleItem).withOptimisticUpdate(
    (store, { id, ...updates }) => {
      const items = store.getQuery(api.gameTitles.listScheduleItems, {});
      const current = items?.find((item) => item._id === id);
      if (!items || !current) return;
      store.setQuery(
        api.gameTitles.listScheduleItems,
        {},
        items.map((item) =>
          item._id === id ? { ...item, ...updates, updatedAt: Date.now() } : item,
        ),
      );
      const details = store.getQuery(api.gameTitles.getDetails, { id: current.gameTitleId });
      if (details) {
        store.setQuery(
          api.gameTitles.getDetails,
          { id: current.gameTitleId },
          {
            ...details,
            scheduleItems: details.scheduleItems.map((item) =>
              item._id === id ? { ...item, ...updates, updatedAt: Date.now() } : item,
            ),
          },
        );
      }
    },
  );
  const removeScheduleItem = useMutation(api.gameTitles.removeScheduleItem).withOptimisticUpdate(
    (store, { id }) => {
      const items = store.getQuery(api.gameTitles.listScheduleItems, {});
      const current = items?.find((item) => item._id === id);
      if (!items || !current) return;
      store.setQuery(
        api.gameTitles.listScheduleItems,
        {},
        items.filter((item) => item._id !== id),
      );
      const details = store.getQuery(api.gameTitles.getDetails, { id: current.gameTitleId });
      if (details) {
        store.setQuery(
          api.gameTitles.getDetails,
          { id: current.gameTitleId },
          {
            ...details,
            scheduleItems: details.scheduleItems.filter((item) => item._id !== id),
          },
        );
      }
    },
  );
  const renameStageByName = useMutation(api.schedule.renameStageByName);
  const seedScheduleStages = useMutation(api.schedule.seedScheduleStages);
  const { groups: stageGroups, stageMap, isLoading: areStagesLoading } = useStageGroups();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [activeDrag, setActiveDrag] = React.useState<TitleDragData | TimelineItemDragData>();
  const [overlayWidth, setOverlayWidth] = React.useState<number>();
  const pointerX = React.useRef<number | null>(null);
  const contextMenuX = React.useRef<number | null>(null);
  const pointerMoveHandler = React.useRef<((event: PointerEvent) => void) | null>(null);
  const safetyCleanup = React.useRef<() => void>(() => {});
  const clearDragRef = React.useRef<() => void>(() => {});
  const edgeScrollFrame = React.useRef<number | null>(null);
  const edgeScrollSpeed = React.useRef(0);
  const dropPreviewRef = React.useRef<HTMLDivElement>(null);
  const resizedItemRef = React.useRef<HTMLElement | null>(null);
  const [isReadOnly, setIsReadOnly] = React.useState(!isAdmin);
  const [titleForm, setTitleForm] = React.useState<TitleForm | null>(null);
  const [selectedGameId, setSelectedGameId] = React.useState<Id<"gameTitles"> | null>(null);
  const [stageRenameForm, setStageRenameForm] = React.useState<StageRenameForm | null>(null);
  const [isSeedingStages, setIsSeedingStages] = React.useState(false);
  const [dayWidth, setDayWidth] = React.useState(getInitialDayWidth);

  React.useEffect(() => {
    if (!isAdmin) setIsReadOnly(true);
  }, [isAdmin]);

  const handleDayWidthChange = React.useCallback((width: number) => {
    const nextWidth = clampDayWidth(width);
    setDayWidth(nextWidth);
    window.localStorage.setItem(DAY_WIDTH_STORAGE_KEY, String(nextWidth));
  }, []);

  const timelineItemsByStage = React.useMemo(() => {
    const map = new Map<string, TimelineItem[]>();

    for (const item of scheduleItems ?? []) {
      if (!item.game) continue;
      const color = item.game.color ?? "#888888";
      const start = dateOnlyFromTimestamp(item.startDate);
      const inclusiveEnd = dateOnlyFromTimestamp(item.endDate);
      const durationDays = differenceInCalendarDays(inclusiveEnd, start) + 1;
      const timelineItem = {
        id: String(item["_id"]),
        title: item.game.name,
        subtitle:
          durationDays === 1
            ? format(start, "MMM d")
            : `${format(start, "MMM d")} – ${format(inclusiveEnd, "MMM d")}`,
        start,
        end: addDays(inclusiveEnd, 1),
        data: {
          scheduleItemId: String(item["_id"]),
          gameTitleId: String(item.gameTitleId),
          color,
        },
        style: getSegmentedGameColorStyle(color, dayWidth, durationDays),
      } satisfies TimelineItem;
      map.set(item.stageName, [...(map.get(item.stageName) ?? []), timelineItem]);
    }

    return map;
  }, [dayWidth, scheduleItems]);

  const timelineGroups = React.useMemo<TimelineGroup[]>(
    () =>
      stageGroups.flatMap(({ section, stages }) =>
        stages.map((stageName) => ({
          id: stageName,
          title: getStageNameLabel(stageName),
          eyebrow: section,
          data: { section, stageName },
          rows: buildLanes(timelineItemsByStage.get(stageName) ?? []).map((items, lane) => ({
            id: `${stageName}:lane:${lane}`,
            label: "",
            items,
          })),
        })),
      ),
    [stageGroups, timelineItemsByStage],
  );

  const sectionForStage = React.useCallback(
    (stageName: string) => stageMap.get(stageName)?.section,
    [stageMap],
  );

  const stopEdgeScroll = React.useCallback(() => {
    edgeScrollSpeed.current = 0;
    if (edgeScrollFrame.current !== null) cancelAnimationFrame(edgeScrollFrame.current);
    edgeScrollFrame.current = null;
  }, []);

  const startEdgeScroll = React.useCallback(() => {
    if (edgeScrollFrame.current !== null) return;

    const tick = () => {
      const scroller = document.querySelector<HTMLElement>("[data-schedule-timeline-scroll]");
      if (!scroller || edgeScrollSpeed.current === 0) {
        edgeScrollFrame.current = null;
        return;
      }

      const previousScrollLeft = scroller.scrollLeft;
      scroller.scrollLeft += edgeScrollSpeed.current;
      if (scroller.scrollLeft === previousScrollLeft) {
        edgeScrollSpeed.current = 0;
        edgeScrollFrame.current = null;
        return;
      }
      edgeScrollFrame.current = requestAnimationFrame(tick);
    };

    edgeScrollFrame.current = requestAnimationFrame(tick);
  }, []);

  const updateEdgeScroll = React.useCallback(
    (clientX: number, clientY: number) => {
      const scroller = document.querySelector<HTMLElement>("[data-schedule-timeline-scroll]");
      if (!scroller) return stopEdgeScroll();

      const rect = scroller.getBoundingClientRect();
      if (
        clientY < rect.top ||
        clientY > rect.bottom ||
        clientX < rect.left ||
        clientX > rect.right
      ) {
        return stopEdgeScroll();
      }

      const timelineLeft = rect.left + TIMELINE_GROUP_COLUMN_WIDTH;
      const leftDistance = clientX - timelineLeft;
      const rightDistance = rect.right - clientX;
      const leftSpeed =
        leftDistance < EDGE_SCROLL_ZONE
          ? -MAX_EDGE_SCROLL_SPEED *
            Math.min(1, (EDGE_SCROLL_ZONE - leftDistance) / EDGE_SCROLL_ZONE)
          : 0;
      const rightSpeed =
        rightDistance < EDGE_SCROLL_ZONE
          ? (MAX_EDGE_SCROLL_SPEED * (EDGE_SCROLL_ZONE - rightDistance)) / EDGE_SCROLL_ZONE
          : 0;

      edgeScrollSpeed.current = leftSpeed || rightSpeed;
      if (edgeScrollSpeed.current) startEdgeScroll();
      else stopEdgeScroll();
    },
    [startEdgeScroll, stopEdgeScroll],
  );

  const handleDragStart = React.useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current as DragData | undefined;
      const activatorEvent = event.activatorEvent;
      safetyCleanup.current();
      const activatorNode =
        activatorEvent.target instanceof Element
          ? activatorEvent.target.closest<HTMLElement>(
              "[data-game-title-library-item], [data-schedule-timeline-item-id]",
            )
          : null;

      pointerX.current = "clientX" in activatorEvent ? Number(activatorEvent.clientX) : null;
      if (pointerMoveHandler.current)
        document.removeEventListener("pointermove", pointerMoveHandler.current);
      pointerMoveHandler.current = (pointerEvent) => {
        pointerX.current = pointerEvent.clientX;
        updateEdgeScroll(pointerEvent.clientX, pointerEvent.clientY);
      };
      document.addEventListener("pointermove", pointerMoveHandler.current);

      const forceClear = () => clearDragRef.current();
      document.addEventListener("pointerup", forceClear, { once: true });
      document.addEventListener("pointercancel", forceClear, { once: true });
      window.addEventListener("blur", forceClear, { once: true });
      safetyCleanup.current = () => {
        document.removeEventListener("pointerup", forceClear);
        document.removeEventListener("pointercancel", forceClear);
        window.removeEventListener("blur", forceClear);
      };

      if (data?.type === "schedule-timeline-resize") {
        resizedItemRef.current = document.querySelector<HTMLElement>(
          `[data-schedule-timeline-item-id="${CSS.escape(data.item.id)}"]`,
        );
        if (resizedItemRef.current) resizedItemRef.current.style.opacity = "0";
      }
      setActiveDrag(
        data?.type === "game-title" || data?.type === "schedule-timeline-item" ? data : undefined,
      );
      setOverlayWidth(activatorNode?.getBoundingClientRect().width);
      updateDropPreview(dropPreviewRef.current, undefined, undefined, dayWidth);
    },
    [dayWidth, updateEdgeScroll],
  );

  const clearDrag = React.useCallback(() => {
    safetyCleanup.current();
    safetyCleanup.current = () => {};
    setActiveDrag(undefined);
    setOverlayWidth(undefined);
    pointerX.current = null;
    if (pointerMoveHandler.current)
      document.removeEventListener("pointermove", pointerMoveHandler.current);
    pointerMoveHandler.current = null;
    stopEdgeScroll();
    if (resizedItemRef.current) resizedItemRef.current.style.opacity = "";
    resizedItemRef.current = null;
    updateDropPreview(dropPreviewRef.current, undefined, undefined, dayWidth);
  }, [dayWidth, stopEdgeScroll]);
  clearDragRef.current = clearDrag;

  const handleDragMove = React.useCallback(
    (event: DragMoveEvent) => {
      const target = getDropTarget(event);
      updateDropPreview(
        dropPreviewRef.current,
        isReadOnly ? undefined : getPreview(event, dayWidth, pointerX.current),
        target?.rect,
        dayWidth,
      );
    },
    [dayWidth, isReadOnly],
  );

  const handleDragEnd = React.useCallback(
    async (event: DragEndEvent) => {
      const nextPreview = getPreview(event, dayWidth, pointerX.current);
      const active = event.active.data.current as DragData | undefined;
      const over = getDropTarget(event)?.data;
      clearDrag();

      if (!active || !isAdmin || isReadOnly) return;
      if (active.type === "schedule-timeline-item" && over?.type === "game-title-library-delete") {
        await removeScheduleItem({
          id: active.item.data!.scheduleItemId as Id<"gameTitleScheduleItems">,
        });
        return;
      }
      if (!nextPreview) return;

      const startAt = dateValue(nextPreview.start);
      const endAt = addDays(dateValue(nextPreview.end), -1);

      if (active.type === "game-title" && over?.type === "schedule-timeline-row") {
        await createScheduleItem({
          gameTitleId: active.id as Id<"gameTitles">,
          stageName: over.groupId,
          section: sectionForStage(over.groupId),
          startDate: timestampFromDateOnly(startAt),
          endDate: timestampFromDateOnly(endAt),
        });
        return;
      }

      if (active.type === "schedule-timeline-item" && over?.type === "schedule-timeline-row") {
        await updateScheduleItem({
          id: active.item.data!.scheduleItemId as Id<"gameTitleScheduleItems">,
          stageName: over.groupId,
          section: sectionForStage(over.groupId),
          startDate: timestampFromDateOnly(startAt),
          endDate: timestampFromDateOnly(endAt),
        });
        return;
      }

      if (active.type === "schedule-timeline-resize") {
        await updateScheduleItem({
          id: active.item.data!.scheduleItemId as Id<"gameTitleScheduleItems">,
          startDate: timestampFromDateOnly(startAt),
          endDate: timestampFromDateOnly(endAt),
        });
      }
    },
    [
      clearDrag,
      createScheduleItem,
      dayWidth,
      isAdmin,
      isReadOnly,
      removeScheduleItem,
      sectionForStage,
      updateScheduleItem,
    ],
  );

  const handleDeleteTitle = React.useCallback(
    async (id: string) => {
      if (!isAdmin) return;
      const confirmed = await confirm({
        title: "Delete game title?",
        description: "This removes the title and all schedule items using it.",
        confirmText: "Delete",
        cancelText: "Cancel",
        confirmButtonVariant: "destructive",
      });
      if (confirmed) await removeTitle({ id: id as Id<"gameTitles"> });
    },
    [confirm, isAdmin, removeTitle],
  );

  const handleDeleteScheduleItem = React.useCallback(
    async (id: string) => {
      if (!isAdmin || isReadOnly) return;
      const confirmed = await confirm({
        title: "Remove from schedule?",
        description:
          "This removes only this scheduled placement. The game title stays in the library.",
        confirmText: "Remove",
        cancelText: "Cancel",
        confirmButtonVariant: "destructive",
      });
      if (confirmed) await removeScheduleItem({ id: id as Id<"gameTitleScheduleItems"> });
    },
    [confirm, isAdmin, isReadOnly, removeScheduleItem],
  );

  const handleOpenGameDetails = React.useCallback((item: TimelineItem) => {
    if (item.data?.gameTitleId) setSelectedGameId(item.data.gameTitleId as Id<"gameTitles">);
  }, []);

  const handleOpenCalltimes = React.useCallback(
    (date: Date) => {
      setSelectedGameId(null);
      void navigate({
        to: "/call-times/$date",
        params: { date: format(date, "yyyy-MM-dd") },
        search: {},
      });
    },
    [navigate],
  );

  const handleOpenItemCalltimes = React.useCallback(
    (item: TimelineItem) => {
      const node = document.querySelector<HTMLElement>(
        `[data-schedule-timeline-item-id="${CSS.escape(item.id)}"]`,
      );
      const offset =
        contextMenuX.current !== null && node
          ? contextMenuX.current - node.getBoundingClientRect().left
          : 0;
      const dayIndex = Math.max(
        0,
        Math.min(getItemDurationDays(item) - 1, Math.floor(offset / dayWidth)),
      );
      handleOpenCalltimes(addDays(dateValue(item.start), dayIndex));
    },
    [dayWidth, handleOpenCalltimes],
  );

  const timelineLayout = React.useMemo(
    () => ({
      groupColumnWidth: TIMELINE_GROUP_COLUMN_WIDTH,
      rowColumnWidth: 0,
      hourWidth: dayWidth,
      rowHeight: TIMELINE_ROW_HEIGHT,
    }),
    [dayWidth],
  );

  const timelineInteraction = React.useMemo<ScheduleTimelineInteraction<ScheduleItemData>>(
    () =>
      isReadOnly
        ? { kind: "readonly", onItemClick: handleOpenGameDetails }
        : {
            kind: "editable",
            resize: true,
            onItemClick: handleOpenGameDetails,
            onItemDelete: (item) => void handleDeleteScheduleItem(item.data!.scheduleItemId),
          },
    [handleDeleteScheduleItem, handleOpenGameDetails, isReadOnly],
  );

  const timelineRender = React.useMemo<
    ScheduleTimelineRenderers<ScheduleItemData, unknown, StageGroupData>
  >(
    () => ({
      itemContextMenu: ({ item }) => (
        <>
          <ContextMenuItem onClick={() => handleOpenGameDetails(item)}>
            <InfoIcon />
            View details
          </ContextMenuItem>
          <ContextMenuItem onClick={() => handleOpenItemCalltimes(item)}>
            <CalendarDaysIcon />
            Go to Call Times
          </ContextMenuItem>
        </>
      ),
      groupLabel: ({ group }) => (
        <StageTimelineLabel
          group={group}
          isReadOnly={isReadOnly}
          onRename={() =>
            setStageRenameForm({
              oldName: group.data!.stageName,
              newName: getStageNameLabel(group.data!.stageName),
            })
          }
        />
      ),
    }),
    [handleOpenGameDetails, handleOpenItemCalltimes, isReadOnly],
  );

  const handleSaveTitle = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!titleForm?.name.trim()) return;

      const payload = {
        name: titleForm.name.trim(),
        color: titleForm.color.trim() || "#888888",
      };

      if (titleForm.id) {
        await updateTitle({ id: titleForm.id as Id<"gameTitles">, ...payload });
      } else {
        await createTitle(payload);
      }

      setTitleForm(null);
    },
    [createTitle, titleForm, updateTitle],
  );

  const handleRenameStage = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (
        !stageRenameForm?.newName.trim() ||
        stageRenameForm.oldName === stageRenameForm.newName.trim()
      ) {
        setStageRenameForm(null);
        return;
      }

      await renameStageByName({
        oldName: stageRenameForm.oldName,
        newName: stageRenameForm.newName.trim(),
      });
      setStageRenameForm(null);
    },
    [renameStageByName, stageRenameForm],
  );

  const handleSeedStages = React.useCallback(async () => {
    if (!isAdmin) return;
    const confirmed = await confirm({
      title: "Initialize stages?",
      description:
        "This will replace current stages with the standard Event Coverage and Media Day layout.",
      confirmText: "Initialize stages",
      cancelText: "Cancel",
      confirmButtonVariant: "destructive",
    });
    if (!confirmed) return;

    setIsSeedingStages(true);
    try {
      await seedScheduleStages({});
    } finally {
      setIsSeedingStages(false);
    }
  }, [confirm, isAdmin, seedScheduleStages]);

  if (!rawGames || !scheduleItems) return <div>Loading...</div>;

  return (
    <DndContext
      autoScroll={false}
      collisionDetection={pointerWithin}
      onDragCancel={clearDrag}
      onDragEnd={(event) => void handleDragEnd(event)}
      onDragMove={handleDragMove}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      <div
        className="flex h-full flex-col gap-4 p-4"
        onContextMenuCapture={(event) => {
          contextMenuX.current = event.clientX;
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">EWC Calendar</h1>
            <p className="text-sm text-muted-foreground">Event schedule and game title placement</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex h-9 items-center gap-2 rounded-md border bg-background px-2 text-xs text-muted-foreground">
              <span className="font-medium whitespace-nowrap text-foreground">Day width</span>
              <input
                aria-label="Day cell width"
                className="h-2 w-32 cursor-ew-resize accent-primary"
                max={MAX_DAY_WIDTH}
                min={MIN_DAY_WIDTH}
                onChange={(event) => handleDayWidthChange(Number(event.currentTarget.value))}
                step={5}
                type="range"
                value={dayWidth}
              />
              <span className="w-11 tabular-nums">{dayWidth}px</span>
              <Button
                onClick={() => handleDayWidthChange(DEFAULT_DAY_WIDTH)}
                size="sm"
                type="button"
                variant="ghost"
              >
                Reset
              </Button>
            </div>
            <Button
              disabled={!isAdmin || isReadOnly}
              onClick={() => setTitleForm({ name: "", color: "#888888" })}
              size="sm"
            >
              <PlusIcon />
              Add title
            </Button>
            <Button
              aria-label={isReadOnly ? "Enable editing" : "Switch to read-only"}
              onClick={() => void (isReadOnly ? setIsReadOnly(false) : setIsReadOnly(true))}
              size="sm"
              variant={isReadOnly ? "destructive" : "outline"}
              disabled={!isAdmin}
            >
              {isReadOnly ? <LockOpenIcon /> : <LockIcon />}
              {!isAdmin ? "Read-only" : isReadOnly ? "Enable editing" : "Read-only"}
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 gap-3 overflow-hidden">
          <TitleLibrary
            games={rawGames}
            isReadOnly={isReadOnly}
            onDelete={handleDeleteTitle}
            showDeleteDrop={activeDrag?.type === "schedule-timeline-item"}
            onEdit={(game) =>
              setTitleForm({
                id: game["_id"] as string,
                name: game.name,
                color: game.color ?? "#888888",
              })
            }
          />

          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
            {areStagesLoading ? (
              <div className="flex h-full items-center justify-center rounded-lg border text-sm text-muted-foreground">
                Loading stages…
              </div>
            ) : stageGroups.length === 0 ? (
              <EmptyStagesState
                disabled={!isAdmin || isSeedingStages}
                isSeeding={isSeedingStages}
                onSeed={() => void handleSeedStages()}
              />
            ) : (
              <ScheduleTimeline
                className="h-full rounded-lg"
                groups={timelineGroups}
                headerBands={TIMELINE_HEADER_BANDS}
                initialScrollValue={INITIAL_SCROLL_DATE}
                interaction={timelineInteraction}
                labels={TIMELINE_LABELS}
                layout={timelineLayout}
                range={TIMELINE_RANGE}
                render={timelineRender}
              />
            )}
          </div>
        </div>

        <GameDetailsSheet
          gameId={selectedGameId}
          getStageLabel={getStageNameLabel}
          onChange={setSelectedGameId}
          onOpenCalltimes={(startDate) => handleOpenCalltimes(dateOnlyFromTimestamp(startDate))}
        />
        <TitleSheet form={titleForm} onChange={setTitleForm} onSubmit={handleSaveTitle} />
        <StageRenameSheet
          form={stageRenameForm}
          onChange={setStageRenameForm}
          onSubmit={handleRenameStage}
        />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed top-0 left-0 z-30 hidden items-center overflow-hidden rounded-md border px-3 py-2 text-sm font-semibold shadow-sm will-change-transform"
        ref={dropPreviewRef}
      />
      <DragOverlay dropAnimation={null} style={{ pointerEvents: "none", willChange: "transform" }}>
        {activeDrag ? (
          <GameTitleDragOverlay dayWidth={dayWidth} drag={activeDrag} width={overlayWidth} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function GameTitleDragOverlay({
  dayWidth,
  drag,
  width,
}: {
  dayWidth: number;
  drag: TitleDragData | TimelineItemDragData;
  width?: number;
}) {
  const content =
    drag.type === "game-title"
      ? { color: drag.color, durationDays: 1, name: drag.name }
      : {
          color: drag.item.data?.color ?? "#888888",
          durationDays: getItemDurationDays(drag.item),
          name: drag.item.title,
        };

  return (
    <div
      className="flex items-center rounded-md border px-2 text-xs font-medium shadow-lg"
      style={{
        ...getSegmentedGameColorStyle(content.color, dayWidth, content.durationDays),
        height: drag.type === "game-title" ? 36 : TIMELINE_ROW_HEIGHT - 12,
        width,
      }}
    >
      <GameLabel name={content.name} showGrip />
    </div>
  );
}

function EmptyStagesState({
  disabled,
  isSeeding,
  onSeed,
}: {
  disabled: boolean;
  isSeeding: boolean;
  onSeed: () => void;
}) {
  return (
    <div className="flex h-full items-center justify-center rounded-lg border bg-background p-6">
      <div className="max-w-sm text-center">
        <h2 className="text-base font-semibold">No stages configured</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Initialize Event Coverage and Media Day stages before placing game titles on the timeline.
        </p>
        <Button className="mt-4" disabled={disabled} onClick={onSeed} size="sm">
          {isSeeding ? "Initializing…" : "Initialize stages"}
        </Button>
      </div>
    </div>
  );
}

function TitleSheet({
  form,
  onChange,
  onSubmit,
}: {
  form: TitleForm | null;
  onChange: (form: TitleForm | null) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <Sheet open={Boolean(form)} onOpenChange={(open) => !open && onChange(null)}>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-md">
        {form ? (
          <form className="flex h-full flex-col" onSubmit={onSubmit}>
            <SheetHeader>
              <SheetTitle>{form.id ? "Edit title" : "Add title"}</SheetTitle>
              <SheetDescription>Master game title name and color.</SheetDescription>
            </SheetHeader>
            <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-4">
              <div className="space-y-2">
                <Label htmlFor="title-name">Name</Label>
                <Input
                  id="title-name"
                  value={form.name}
                  onChange={(event) => onChange({ ...form, name: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title-color">Color</Label>
                <Input
                  id="title-color"
                  value={form.color}
                  onChange={(event) => onChange({ ...form, color: event.target.value })}
                />
              </div>
            </div>
            <SheetFooter className="border-t">
              <Button type="submit">Save</Button>
              <Button type="button" variant="outline" onClick={() => onChange(null)}>
                Cancel
              </Button>
            </SheetFooter>
          </form>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function StageRenameSheet({
  form,
  onChange,
  onSubmit,
}: {
  form: StageRenameForm | null;
  onChange: (form: StageRenameForm | null) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <Sheet open={Boolean(form)} onOpenChange={(open) => !open && onChange(null)}>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-md">
        {form ? (
          <form className="flex h-full flex-col" onSubmit={onSubmit}>
            <SheetHeader>
              <SheetTitle>Rename stage</SheetTitle>
              <SheetDescription>
                This renames the stage everywhere, including existing schedule items.
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-4">
              <div className="space-y-2">
                <Label htmlFor="stage-name">Stage name</Label>
                <Input
                  autoFocus
                  id="stage-name"
                  value={form.newName}
                  onChange={(event) => onChange({ ...form, newName: event.target.value })}
                />
              </div>
            </div>
            <SheetFooter className="border-t">
              <Button type="submit">Save</Button>
              <Button type="button" variant="outline" onClick={() => onChange(null)}>
                Cancel
              </Button>
            </SheetFooter>
          </form>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function StageTimelineLabel({
  group,
  isReadOnly,
  onRename,
}: {
  group: TimelineGroup;
  isReadOnly: boolean;
  onRename: () => void;
}) {
  return (
    <ContextMenu>
      <ContextMenuTrigger className="block h-full w-full">
        <div className="flex h-full min-w-0 flex-col justify-center px-3 py-2">
          <span className="truncate text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
            {group.data?.section}
          </span>
          <span className="truncate text-sm font-semibold">{group.title}</span>
          {group.rows.length > 1 ? (
            <span className="text-[10px] text-muted-foreground">
              {group.rows.length} overlapping lanes
            </span>
          ) : null}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem disabled={isReadOnly} onClick={onRename}>
          <PencilIcon className="text-muted-foreground" size={16} />
          Rename stage
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function TitleLibrary({
  games,
  isReadOnly,
  onEdit,
  onDelete,
  showDeleteDrop,
}: {
  games: GameTitle[];
  isReadOnly: boolean;
  onEdit: (game: GameTitle) => void;
  onDelete: (id: string) => void;
  showDeleteDrop: boolean;
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: "game-title-library-delete",
    data: { type: "game-title-library-delete" } satisfies LibraryDeleteDropData,
    disabled: isReadOnly,
  });

  return (
    <aside
      className="relative flex w-64 shrink-0 flex-col overflow-hidden rounded-lg border bg-background"
      ref={setNodeRef}
    >
      <div className="border-b p-3">
        <h2 className="text-sm font-semibold">Game title library</h2>
        <p className="text-xs text-muted-foreground">Drag a title onto the schedule.</p>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {games.map((game) => (
          <TitleLibraryItem
            game={game}
            isReadOnly={isReadOnly}
            key={game["_id"]}
            onDelete={onDelete}
            onEdit={onEdit}
          />
        ))}
      </div>
      {showDeleteDrop ? (
        <div
          className={`pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center rounded-md border-2 border-dashed p-4 text-center transition-colors ${isOver ? "text-destructive-foreground border-destructive bg-destructive" : "border-muted-foreground/50 bg-background/95 text-muted-foreground"}`}
        >
          <TrashIcon className="mb-2 size-5" />
          <span className="text-sm font-semibold">Drop here to delete</span>
          <span className="mt-1 text-xs">Removes this placement from the calendar</span>
        </div>
      ) : null}
    </aside>
  );
}

function TitleLibraryItem({
  game,
  isReadOnly,
  onEdit,
  onDelete,
}: {
  game: GameTitle;
  isReadOnly: boolean;
  onEdit: (game: GameTitle) => void;
  onDelete: (id: string) => void;
}) {
  const color = game.color ?? "#888888";
  const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
    id: `title-${game["_id"]}`,
    data: {
      type: "game-title",
      id: game["_id"],
      name: game.name,
      color,
    } satisfies TitleDragData,
    disabled: isReadOnly,
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <div
          className={`rounded-md border p-2 text-xs shadow-xs outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background ${isReadOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing"} ${isDragging ? "opacity-30" : ""}`}
          data-game-title-library-item=""
          ref={setNodeRef}
          style={getGameColorStyle(color)}
          {...attributes}
          {...listeners}
        >
          <GameLabel name={game.name} showGrip={!isReadOnly} />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem disabled={isReadOnly} onClick={() => onEdit(game)}>
          <PencilIcon className="text-muted-foreground" size={16} />
          Edit title
        </ContextMenuItem>
        <ContextMenuItem
          className="text-destructive"
          disabled={isReadOnly}
          onClick={() => onDelete(game["_id"])}
        >
          <TrashIcon className="text-destructive" size={16} />
          Delete title
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function GameLabel({ name, showGrip }: { name: string; showGrip: boolean }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {showGrip ? <GripVerticalIcon className="size-4 shrink-0 opacity-50" /> : null}
      <span className="truncate font-medium">{name}</span>
    </div>
  );
}
